import React, { Fragment, useEffect, useRef, useState } from 'react'

import { show } from '@intercom/messenger-js-sdk'
import {
  IconSettings,
  IconX,
  IconPlus,
  IconMessage,
  IconChevronDown,
} from '@tabler/icons-react'
import { Collapse, Tooltip, Typography } from 'antd'
import Link from 'next/link'
import styled, { css } from 'styled-components'

import { FadedDivider, HAZE_SIZE } from '../shared-components/FadedDivider'

import { CorePageSideNavWrapper, SIDE_WIDTH } from './CorePageSideNavWrapper'

import { FavouritesSelectModal } from '@/components/FavouritesSelectModal'
import { CorePageSideNavSettings } from '@/components/layout-components/CorePageSideNavSettings'
import { ProjectSelector } from '@/components/layout-components/ProjectSelector'
import { Button } from '@/components/shared-components/BUI/Button'
import { useLocale } from '@/context/localeContext'
import { useNavigation } from '@/context/navigationContext'
import { useHasRoles } from '@/hooks/useHasRoles'
import { useResizeable } from '@/hooks/useResizeable'
import { Role } from '@/modules/adminUsers/roles'
import { getDataTags } from '@/utils/index'
import { NavItem } from '@/utils/navigationConfig'

const LS_MENU_WIDTH_KEY = 'buildpass-menu-width'
export const MENU_OVERLAY_PORTAL_ID = 'bp-menu-overlay-portal'

const sectionPadding = css`
  padding-left: 0.5rem;
  padding-right: 0.5rem;
`

const navItemPadding = css`
  padding: 0.4rem 0.3rem 0.4rem 1rem;
`

const Wrapper = styled.aside`
  grid-area: side;
  position: sticky;
  top: 0;
  background-color: ${({ theme }) => theme.colour.neutral.white};
  display: flex;
  flex-direction: column;
  z-index: 1039; // above the project map and below ant's modal

  ${({ theme: { media } }) => media.md`
    border-right: 1px solid ${({ theme }) => theme.colour.neutral.border};
  `}
`

const ResizeBar = styled.div`
  position: absolute;
  top: 0;
  right: -4px;
  width: 4px;
  height: 100%;
  cursor: col-resize;
  user-select: none;
  z-index: 1;
  display: none;

  &:hover {
    background-color: ${({ theme }) => theme.colour.secondary._50};
  }

  ${({ theme }) => theme.media.md`
    display: block;
  `}
`

const ProjectSelectorSection = styled.section`
  padding: 1rem;
  display: flex;
  gap: 1rem;
`

const MainSection = styled.section`
  position: relative; // for menu overlay
  flex-grow: 1;
  flex-shrink: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const CustomChevron = styled(IconChevronDown)<{ $isActive: boolean }>`
  color: #a3a3a3;
  transition: 0.2s;
  transform: rotate(${(props) => (props.$isActive ? '-180' : '0')}deg)
    translateY(-50%) !important;
  transform-origin: 50% 0;
`

const MenuSection = styled.section`
  ${sectionPadding}
  padding-bottom: ${HAZE_SIZE};
  overflow: auto;
  display: flex;
  flex-direction: column;

  > * {
    margin-bottom: 0.75rem;
  }
`

const sectionTitle = css`
  ${({ theme }) => theme.font.caption02};
  width: 100%;
  color: ${({ theme }) => theme.colour.secondary._400};

  padding: 0.4rem 0.5rem 0.4rem 1rem;
  margin-top: 2px;

  white-space: nowrap;

  &:hover {
    background-color: ${({ theme }) => theme.colour.neutral.surface};
    border-radius: ${({ theme }) => theme.borderRadius.small};
  }

  &:first-child {
    margin-top: 0;
  }
`

const SectionTitle = styled(Typography.Text)`
  ${sectionTitle}
  display: flex;
  align-items: center;
  font-weight: 500;
`

type NavItemProps = {
  active: boolean
  iconColour?: string
  iconRotate?: number
  noIcon?: boolean
}

const NavItemSpan = styled.span<NavItemProps>`
  ${navItemPadding}
  margin-top: 2px;
  font-weight: 500;
  color: ${() => '#404040'};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  display: grid;
  grid-template-columns: ${({ noIcon }) => (noIcon ? '1fr' : '1rem 1fr')};
  gap: 0.625rem;
  align-items: center;
  cursor: pointer;

  & svg {
    font-size: 1rem;
    stroke-width: 2px;
    color: ${({ iconColour }) => iconColour || '#737373'};
    transform: rotate(${({ iconRotate }) => iconRotate ?? 0}deg);
  }

  &:hover,
  &:focus,
  &:active {
    background-color: ${() => '#f5f5f5'};
  }

  ${({ active, theme }) =>
    active &&
    css`
      &:not([data-group='true']) {
        color: ${({ theme }) => theme.colour.neutral.darkText};
        background-color: ${() => '#f5f5f5'};

        & svg {
          color: ${({ theme }) => theme.colour.neutral.darkText};
        }

        &:hover,
        &:focus,
        &:active {
          color: ${theme.colour.neutral.darkText};
          background-color: ${() => '#e5e5e5'};
        }
      }
    `}
`

const Text = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const Extra = styled.span`
  display: inline-block;
  margin-left: auto;
`

const CustomCollapse = styled(Collapse)`
  .ant-collapse-item {
    .ant-collapse-header {
      padding: 0;
      border-radius: ${({ theme }) => theme.borderRadius.small} !important;

      & ${NavItemSpan} {
        padding-right: 0.875rem;
      }
    }
  }

  .ant-collapse-content {
    .ant-collapse-content-box {
      padding: 0 !important;
    }
  }
`

const SettingsSection = styled.section`
  ${sectionPadding}
  position: relative;
  padding-bottom: 1rem;
`

const CloseButton = styled(Button).attrs({
  variant: 'secondary-text',
  shape: 'circle',
  icon: <IconX />,
})`
  margin-top: -0.5rem;
  margin-right: -0.5rem;
  color: ${({ theme }) => theme.colour.secondary._400};
`

const FavouritesHeader = styled.button`
  ${sectionTitle}
  padding-bottom: 6px;
  padding-top: 6px;
  padding-right: 16px;
  margin-bottom: 0;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  border: none;
  background-color: transparent;

  border-radius: ${({ theme }) => theme.borderRadius.small};

  & ${SectionTitle} {
    margin: 0;
    padding: 0;
    width: fit-content;
  }

  & svg.hideable {
    font-size: 0.75rem;
    color: ${({ theme }) => theme.colour.secondary._400};
    opacity: 0;
    transition:
      opacity 0.15s,
      background-color 0.5s ease;
    border-radius: ${({ theme }) => theme.borderRadius.small};
    box-sizing: content-box;
    padding: 4px;

    &:hover {
      transform: rotate(90deg);
      transition: transform 0.3s ease;
    }
  }

  &:hover,
  &:focus,
  &:active {
    color: ${({ theme }) => theme.colour.secondary._800};
    background-color: ${({ theme }) => theme.colour.neutral.surface};

    & svg {
      visibility: visible;
      opacity: 1;
      transition: opacity 0.15s;
    }
  }

  & svg.hideable:hover {
    background-color: ${({ theme }) => theme.colour.neutral.border};
    color: ${({ theme }) => theme.colour.secondary._600};
  }
`

const isOpenRecursive = (item: NavItem, currentNavItemId: string) => {
  if (!currentNavItemId) return false

  if (item.id === currentNavItemId) return true

  return item.subItems?.some((i) => isOpenRecursive(i, currentNavItemId))
}

const DEFAULT_OPEN = ['support']

type NavItemRendererProps = {
  item: NavItem
  currentNavItem: NavItem
  level?: number
  noIcon?: boolean
}

const NavItemRenderer: React.FC<NavItemRendererProps> = ({
  item,
  currentNavItem,
  level = 0,
  noIcon = false,
}) => {
  const { t } = useLocale()

  if (item.subItems?.length) {
    const open =
      DEFAULT_OPEN.includes(item.id) ||
      isOpenRecursive(item, currentNavItem?.id)

    return (
      <CustomCollapse
        ghost
        key={item.id}
        bordered={false}
        defaultActiveKey={open ? item.id : null}
        style={{ paddingLeft: `${level}rem` }}
        expandIconPosition="end"
        expandIcon={({ isActive }) => (
          <CustomChevron size={16} $isActive={isActive} />
        )}
      >
        <Collapse.Panel
          key={item.id}
          header={
            <Tooltip
              title={
                item.formerlyKnownAs &&
                `Previously called ${item.formerlyKnownAs}`
              }
              placement="right"
            >
              <NavItemSpan
                iconColour={item.iconColour}
                iconRotate={item.iconRotate}
                active={open}
                data-group="true"
                {...getDataTags({ name: item.id, type: 'navigation' })}
                noIcon={noIcon}
              >
                {item.icon ? (
                  <item.icon size={18} color={item.iconColour} />
                ) : noIcon ? null : (
                  <span />
                )}
                <Text>
                  {t(item.title)}
                  <Extra>{item.extra}</Extra>
                </Text>
              </NavItemSpan>
            </Tooltip>
          }
        >
          {item.subItems.map((subItem) => (
            <NavItemRenderer
              key={subItem.id}
              item={subItem}
              currentNavItem={currentNavItem}
              level={level + 1}
              noIcon={noIcon}
            />
          ))}
        </Collapse.Panel>
      </CustomCollapse>
    )
  }

  if (!item.path) return null

  const active = item.id === currentNavItem?.id

  return (
    <Link
      href={item.path}
      key={item.id}
      {...getDataTags({ name: item.id, type: 'navigation' })}
    >
      <Tooltip
        title={
          item.formerlyKnownAs && `Previously called ${item.formerlyKnownAs}`
        }
        placement="right"
      >
        <NavItemSpan
          iconColour={item.iconColour}
          iconRotate={item.iconRotate}
          active={active}
          data-active={active}
          noIcon={noIcon}
        >
          {item.icon ? <item.icon size={18} /> : noIcon ? null : <span />}
          <Text>
            {t(item.title)}
            <Extra>{item.extra}</Extra>
          </Text>
        </NavItemSpan>
      </Tooltip>
    </Link>
  )
}

const NavigationSection: React.FC<{
  group: NavItem
  currentNavItem: NavItem
  noIcon?: boolean
}> = ({ group, currentNavItem, noIcon = false }) => {
  const { isSectionExpanded, setSectionExpanded } = useNavigation()
  const expanded = isSectionExpanded(group.id)

  return (
    <CustomCollapse
      key={group.id}
      ghost
      bordered={false}
      defaultActiveKey={expanded ? [group.id] : []}
      expandIcon={() => null}
      onChange={(keys) => setSectionExpanded(group.id, keys.length > 0)}
    >
      <Collapse.Panel
        key={group.id}
        header={<SectionTitle>{group.title}</SectionTitle>}
      >
        {group.items.map((item) => (
          <NavItemRenderer
            key={item.id}
            item={item}
            currentNavItem={currentNavItem}
            noIcon={noIcon}
          />
        ))}
      </Collapse.Panel>
    </CustomCollapse>
  )
}

type Props = {
  drawerOpen: boolean
  forceDrawer?: boolean
  closeDrawer: () => void
}

export const CorePageSideNav: React.FC<Props> = ({
  drawerOpen,
  forceDrawer,
  closeDrawer,
}) => {
  const { t } = useLocale()
  const menuRef = useRef<HTMLDivElement>(undefined)
  const {
    navigationConfig,
    currentNavItem,
    isSectionExpanded,
    setSectionExpanded,
  } = useNavigation()
  const [height, setHeight] = useState('100vh')
  const [width, mouseDown, doubleClick] = useResizeable(
    120,
    600,
    SIDE_WIDTH,
    LS_MENU_WIDTH_KEY
  )
  const [favouritesOpen, setFavouritesOpen] = useState(false)
  const isCustomUser = useHasRoles(Role.Custom)

  // scroll to the active element
  useEffect(() => {
    const menuEl = menuRef.current
    const activeItem = menuEl?.querySelector(`[data-active="true"]`)

    if (activeItem) {
      const { height } = menuEl.getBoundingClientRect()
      const difference = height - activeItem.getBoundingClientRect().top

      if (difference < 0) {
        menuEl.scrollTop = Math.abs(difference) + 20 // wee bit of padding
      }
    }
  }, [])

  // correct height with floating address bars on mobile devices
  useEffect(() => {
    const appHeight = () => setHeight(`${window.innerHeight}px`)
    window.addEventListener('resize', appHeight)
    appHeight()

    return () => {
      window.removeEventListener('resize', appHeight)
    }
  }, [])

  const getSupport = () => {
    show()
  }

  const favsToRender = navigationConfig?.find(
    (group) => group.id === 'favourites'
  )

  return (
    <CorePageSideNavWrapper
      open={drawerOpen}
      forceDrawer={forceDrawer}
      onClose={closeDrawer}
    >
      {(isDrawer) => (
        <Wrapper
          id="bp-side-nav"
          style={{ width: isDrawer ? '100%' : `${width}px`, height }}
        >
          <ProjectSelectorSection>
            <ProjectSelector
              getPopupContainer={() => document.getElementById('bp-side-nav')}
            />
            {isDrawer && <CloseButton onClick={closeDrawer} />}
          </ProjectSelectorSection>

          <MainSection>
            <MenuSection ref={menuRef}>
              {/* General Section */}
              {navigationConfig
                ?.filter((group) => group.id === 'general')
                .map((group) => (
                  <NavigationSection
                    key={group.id}
                    group={group}
                    currentNavItem={currentNavItem}
                  />
                ))}

              {/* Favourites Section */}
              {!isCustomUser && (
                <CustomCollapse
                  key="favourites"
                  ghost
                  bordered={false}
                  defaultActiveKey={
                    isSectionExpanded('favourites') ? ['favourites'] : []
                  }
                  expandIcon={() => null}
                  onChange={(keys) =>
                    setSectionExpanded('favourites', keys.length > 0)
                  }
                >
                  <Collapse.Panel
                    key="favourites"
                    header={
                      <FavouritesHeader>
                        <SectionTitle>
                          {t('common:navigation.favouritesTitle')}
                        </SectionTitle>
                        <IconPlus
                          size={14}
                          className="hideable"
                          onClick={(e) => {
                            e.stopPropagation()
                            setFavouritesOpen(true)
                          }}
                        />
                      </FavouritesHeader>
                    }
                  >
                    {favsToRender && (
                      <Fragment>
                        {favsToRender.items.map((item) => (
                          <NavItemRenderer
                            key={item.id}
                            item={item}
                            currentNavItem={currentNavItem}
                            noIcon
                          />
                        ))}
                      </Fragment>
                    )}
                  </Collapse.Panel>
                </CustomCollapse>
              )}

              {/* Other Sections */}
              {navigationConfig
                ?.filter(
                  (group) => !['favourites', 'general'].includes(group.id)
                )
                .map((group) => (
                  <NavigationSection
                    key={group.id}
                    group={group}
                    currentNavItem={currentNavItem}
                  />
                ))}
            </MenuSection>

            <div id={MENU_OVERLAY_PORTAL_ID} />
          </MainSection>

          <SettingsSection>
            <FadedDivider />
            <CorePageSideNavSettings>
              <NavItemSpan as="span" active={false}>
                <IconSettings size={16} /> Settings
              </NavItemSpan>
            </CorePageSideNavSettings>

            <NavItemSpan active={false} onClick={getSupport}>
              <IconMessage size={16} /> Support
            </NavItemSpan>
          </SettingsSection>

          <ResizeBar onMouseDown={mouseDown} onDoubleClick={doubleClick} />
          <FavouritesSelectModal
            open={favouritesOpen}
            closeModal={() => setFavouritesOpen(false)}
          />
        </Wrapper>
      )}
    </CorePageSideNavWrapper>
  )
}
